// src/routes/adminRoutes.js
import React, { useEffect, useState } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { Outlet } from 'react-router-dom';

import ProtectedRoute from '../components/ProtectedRoute';
import AdminMessagingLauncher from '../components/AdminMessagingLauncher';
import WorkflowAlertToast from '../components/WorkflowAlertToast';
import AdminSidebar from '../views/Admin/AdminSidebar';
import AdminTopBar from '../views/Admin/AdminTopBar';
import AdminMobileNav from '../views/Admin/AdminMobileNav';
import AdminClients from '../views/Admin/AdminClients';
import AdminProposal from '../views/Admin/AdminProposal';
import LeadsList from '../views/Admin/LeadsList';
import LeadFunnelReport from '../views/Admin/LeadFunnelReport';
import AdminInvoices from '../views/Admin/AdminInvoices';
import ClientLayout from '../views/Admin/ClientLayout';
import ClientEmail from '../views/Admin/ClientEmail';
import ClientProfile from '../views/Shared/ClientProfile';
import LandingAdmin from '../views/Admin/LandingAdmin';
import AllInvoices from '../views/Admin/AllInvoices';
import AllProposals from '../views/Admin/AllProposals';
import AdminDashboard from '../views/Admin/AdminDashboard';
import ClientTracking from '../views/Admin/ClientTracking';
import AdminSales from '../views/Admin/AdminSales';
import ServiceDesk from '../views/Admin/ServiceDesk';
import AdminClientService from '../views/Admin/AdminClientService';
import AdminClientMaintenance from '../views/Admin/AdminClientMaintenance';
import AdminScheduler from '../views/Admin/AdminScheduler';
import ClientAgenda from '../views/Admin/ClientAgenda';
import AdminTeam from '../views/Admin/AdminTeam';
import AdminCloverSettings from '../views/Admin/AdminCloverSettings';
import AdminMarketingConnections from '../views/Admin/AdminMarketingConnections';
import AdminMetaLeadAdsSettings from '../views/Admin/AdminMetaLeadAdsSettings';
import AdminWhatsAppInbox from '../views/Admin/AdminWhatsAppInbox';
import AdminMarketing from '../views/Admin/AdminMarketing';
import AdminMarketingCampaigns from '../views/Admin/AdminMarketingCampaigns';
import AdminProspecting from '../views/Admin/AdminProspecting';
import AdminMetaAds from '../views/Admin/AdminMetaAds';
import AdminHosting from '../views/Admin/AdminHosting';
import AdminSearchConsole from '../views/Admin/AdminSearchConsole';
import StaffProfile from '../views/Staff/StaffProfile';


const SIDEBAR_COLLAPSE_KEY = 'admin_sidebar_collapsed';

const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      // Sin localStorage (modo privado, etc.) — el toggle sigue funcionando,
      // solo no se recuerda entre sesiones.
    }
  }, [collapsed]);

  return (
    <ProtectedRoute allowedRoles={['admin']}>
      <div className="min-h-screen bg-gray-50">
        <AdminSidebar collapsed={collapsed} onToggleCollapse={() => setCollapsed((c) => !c)} />
        <div className={`flex min-h-screen flex-col transition-all duration-200 ${collapsed ? 'md:pl-[76px]' : 'md:pl-64'}`}>
          <AdminTopBar />
          {/* Sin max-width ni padding aquí a propósito — cada vista ya trae
              el suyo (algunas hasta 1740px, ver ADMIN_PAGE_WIDTH), y antes
              del sidebar no había ningún contenedor global encerrándolas. */}
          <main className="flex-1 pb-20 md:pb-6">
            <Outlet />
          </main>
        </div>
        <AdminMobileNav />
        <AdminMessagingLauncher />
        <WorkflowAlertToast />
      </div>
    </ProtectedRoute>
  );
};

const adminRoutes = (
  <Route path="/admin" element={<AdminLayout />}>
    {/* Home del panel */}
    <Route index element={<AdminDashboard />} />              {/* 👈 ahora /admin muestra el dashboard */}
    <Route path="dashboard" element={<AdminDashboard />} />   {/* 👈 acceso directo /admin/dashboard */}

    <Route path="clients" element={<AdminClients />} />
    <Route path="calendar" element={<AdminScheduler />} />
    <Route path="sales" element={<AdminSales />} />
    <Route path="services" element={<ServiceDesk />} />
    <Route path="team" element={<AdminTeam />} />
    <Route path="profile" element={<StaffProfile embedded />} />
    <Route path="settings/clover" element={<AdminCloverSettings />} />
    <Route path="settings/marketing" element={<AdminMarketingConnections />} />
    <Route path="settings/meta-leads" element={<AdminMetaLeadAdsSettings />} />
    <Route path="settings/meta-ads" element={<AdminMetaAds settingsOnly />} />
    <Route path="hosting" element={<AdminHosting />} />
    <Route path="seo" element={<AdminSearchConsole />} />
    <Route path="clients/:clientId" element={<ClientLayout />}>
      <Route path="tracking" element={<ClientTracking />} />
      <Route path="proposal" element={<AdminProposal />} />
      <Route path="invoices" element={<AdminInvoices />} />
      <Route path="email" element={<ClientEmail />} />
      <Route path="profile" element={<ClientProfile />} />
      <Route path="agenda" element={<ClientAgenda />} />
      <Route path="service" element={<AdminClientService />} />
      <Route path="maintenance" element={<AdminClientMaintenance />} />
    </Route>

    <Route path="leads" element={<LeadsList />} />
    <Route path="leads/embudo" element={<LeadFunnelReport />} />
    <Route path="whatsapp" element={<AdminWhatsAppInbox />} />
    <Route path="marketing" element={<AdminMarketing />} />
    <Route path="marketing/campanas" element={<AdminMarketingCampaigns />} />
    <Route path="marketing/prospeccion" element={<AdminProspecting />} />
    <Route path="marketing/meta-ads" element={<AdminMetaAds />} />
    <Route path="marketing/landings" element={<AdminSearchConsole landingStudio />} />
    <Route path="marketing/estudio-ia" element={<Navigate to="/admin/marketing/meta-ads" replace />} />
    <Route path="landing-editor" element={<LandingAdmin />} />
    <Route path="invoices-global" element={<AllInvoices />} />
    <Route path="proposals-global" element={<AllProposals />} />
    <Route path="*" element={<h2>Ruta admin no encontrada</h2>} />
  </Route>
);

export default adminRoutes;
